import styled from '@emotion/styled';
import is from '@sindresorhus/is';
import { format, isToday, isYesterday, parseISO } from 'date-fns';
import type { ReactElement, ReactNode } from 'react';
import { Fragment, useContext, useMemo } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import {
    type ChangeLogEvent,
    type ChangeLogEventArchivedDetail,
    type ChangeLogEventAssignReviewerDetail,
    type ChangeLogEventCommentCreatedDetail,
    type ChangeLogEventCommentResolvedDetail,
    type ChangeLogEventCopiedDetail,
    type ChangeLogEventCreateDetail,
    type ChangeLogEventDeleteDetail,
    type ChangeLogEventDetails,
    type ChangeLogEventEditDetail,
    type ChangeLogEventMoveDetail,
    type ChangeLogEventPublishDetail,
    type ChangeLogEventReviewDetail,
    type ChangeLogEventSkillBulkRemovedDetail,
    type ChangeLogEventSkillsUpdatedDetail,
    type ChangeLogEventTaskCreatedDetail,
    type ChangeLogEventTaskDeletedDetail,
    type ChangeLogEventTaskEditedDetail,
    type ChangeLogEventTaskSkillsUpdatedDetail,
    type ChangeLogEventTasksReorderedDetail,
    type ChangeLogEventUnarchivedDetail,
    type ChangeLogEventUnassignReviewerDetail,
    type ChangeLogEventVersionCreatedDetail,
    isKnownChangeLogEventDetail,
    type PaginatedJobArchitectureHistory,
} from '~/shared/data-contract/job-architecture-history';
import type { LanguageConfig } from '~/shared/internationalization';
import type { Translate } from '~/shared/internationalization/translation';
import {
    IconArchive,
    IconArrowsRepeat,
    IconChatBubble7,
    IconCircleCheck,
    IconCirclePlus,
    IconPencil,
    IconPeople,
    IconSend,
    IconSkills,
    IconTasks,
    IconTrashCan,
    IconUnarchive,
} from '~/ui/core/icons';

import { useJobArchitectureHistory } from '../api/use-job-architecture-history';

const BoldText = styled.span(({ theme }) => ({
    ...theme.typography.body.S.semibold,
}));

export type TimelineFooterItem = {
    readonly content: string;
    readonly key: string;
};

export type TimelineItem = {
    readonly id: string;
    readonly entityId: string | null;
    readonly entityType: 'cluster' | 'family' | 'level' | 'role' | 'root';
    readonly eventDetails: ChangeLogEventDetails;
    readonly footerItems: readonly TimelineFooterItem[];
    readonly icon: ReactElement;
    readonly subtitle?: ReactNode;
    readonly title: ReactNode;
};

export type TimelineGroup = {
    readonly date: string;
    readonly isLastGroup: boolean;
    readonly items: readonly TimelineItem[];
    readonly label: string;
};

type UseChangelogDataReturn = {
    readonly hasNextPage: boolean;
    readonly isLoading: boolean;
    readonly LoadMoreCursor: ReactElement | null;
    readonly timelineGroups: readonly TimelineGroup[];
};

const formatDate = (timestamp: string): string => {
    return format(new Date(timestamp), 'yyyy-MM-dd');
};

const isArchivedDetail = (details: ChangeLogEventDetails): details is ChangeLogEventArchivedDetail => {
    return details.type === 'archived';
};

const isCreateDetail = (details: ChangeLogEventDetails): details is ChangeLogEventCreateDetail => {
    return details.type === 'created';
};

const isDeleteDetail = (details: ChangeLogEventDetails): details is ChangeLogEventDeleteDetail => {
    return details.type === 'deleted';
};

const isEditDetail = (details: ChangeLogEventDetails): details is ChangeLogEventEditDetail => {
    return details.type === 'edited';
};

const isMoveDetail = (details: ChangeLogEventDetails): details is ChangeLogEventMoveDetail => {
    return details.type === 'moved';
};

const isAssignReviewerDetail = (details: ChangeLogEventDetails): details is ChangeLogEventAssignReviewerDetail => {
    return details.type === 'assigned_reviewer';
};

const isUnassignReviewerDetail = (details: ChangeLogEventDetails): details is ChangeLogEventUnassignReviewerDetail => {
    return details.type === 'unassigned_reviewer';
};

const isReviewDetail = (details: ChangeLogEventDetails): details is ChangeLogEventReviewDetail => {
    return details.type === 'reviewed';
};

const isPublishDetail = (details: ChangeLogEventDetails): details is ChangeLogEventPublishDetail => {
    return details.type === 'published';
};

const isCopiedDetail = (details: ChangeLogEventDetails): details is ChangeLogEventCopiedDetail => {
    return details.type === 'copied';
};

const isUnarchivedDetail = (details: ChangeLogEventDetails): details is ChangeLogEventUnarchivedDetail => {
    return details.type === 'unarchived';
};

const isSkillsUpdatedDetail = (details: ChangeLogEventDetails): details is ChangeLogEventSkillsUpdatedDetail => {
    return details.type === 'skills_updated';
};

const isVersionCreatedDetail = (details: ChangeLogEventDetails): details is ChangeLogEventVersionCreatedDetail => {
    return details.type === 'version_created';
};

const isSkillBulkRemovedDetail = (details: ChangeLogEventDetails): details is ChangeLogEventSkillBulkRemovedDetail => {
    return details.type === 'skill_bulk_removed';
};

const isTaskCreatedDetail = (details: ChangeLogEventDetails): details is ChangeLogEventTaskCreatedDetail => {
    return details.type === 'task_created';
};

const isTaskEditedDetail = (details: ChangeLogEventDetails): details is ChangeLogEventTaskEditedDetail => {
    return details.type === 'task_edited';
};

const isTaskDeletedDetail = (details: ChangeLogEventDetails): details is ChangeLogEventTaskDeletedDetail => {
    return details.type === 'task_deleted';
};

const isTasksReorderedDetail = (details: ChangeLogEventDetails): details is ChangeLogEventTasksReorderedDetail => {
    return details.type === 'tasks_reordered';
};

const isTaskSkillsUpdatedDetail = (
    details: ChangeLogEventDetails,
): details is ChangeLogEventTaskSkillsUpdatedDetail => {
    return details.type === 'task_skills_updated';
};

const isCommentCreatedDetail = (details: ChangeLogEventDetails): details is ChangeLogEventCommentCreatedDetail => {
    return details.type === 'comment_created';
};

const isCommentResolvedDetail = (details: ChangeLogEventDetails): details is ChangeLogEventCommentResolvedDetail => {
    return details.type === 'comment_resolved';
};

const createBoldElement = (text: string): ReactNode => {
    return <BoldText>{text}</BoldText>;
};

const getEntityTypeLabel = (entityType: string, translate: Translate): string => {
    const typeMap: Record<string, string> = {
        cluster: translate('pages.job-architecture-v2.job-cluster'),
        family: translate('pages.job-architecture-v2.job-family'),
        level: translate('pages.job-architecture-v2.job-level'),
        role: translate('pages.job-architecture-v2.job-role'),
        root: translate('pages.job-architecture-v2.changelog.entity-types.root'),
    };

    return typeMap[entityType] ?? entityType;
};

const formatEntityWithType = (
    entity: { readonly name: string; readonly type?: string },
    fallbackType: string,
    translate: Translate,
): ReactNode => {
    const entityType = entity.type ?? fallbackType;
    const typeLabel = getEntityTypeLabel(entityType, translate);

    return (
        <Fragment>
            {createBoldElement(entity.name)} {typeLabel}
        </Fragment>
    );
};

const formatUserName = (
    user: { readonly firstName: string; readonly lastName: string } | null | undefined,
    translate: Translate,
): string => {
    if (is.nullOrUndefined(user)) {
        return translate('pages.job-architecture-v2.changelog.unknown-user');
    }

    const fullName = `${user.firstName} ${user.lastName}`.trim();

    return is.nonEmptyString(fullName) ? fullName : translate('pages.job-architecture-v2.changelog.unknown-user');
};

const TITLE_GENERATORS: Record<
    string,
    (details: ChangeLogEventDetails, entityType: string, translate: Translate) => ReactNode
> = {
    archived: (details, entityType, translate) => {
        if (!isArchivedDetail(details)) {
            return null;
        }
        const entityNode = formatEntityWithType(details.entity, entityType, translate);

        return (
            <Fragment>
                {translate('pages.job-architecture-v2.changelog.actions.archived')} {entityNode}
            </Fragment>
        );
    },
    assigned_reviewer: (details, entityType, translate) => {
        if (!isAssignReviewerDetail(details)) {
            return null;
        }
        const reviewer = formatUserName(details.reviewer, translate);
        const entityNode = formatEntityWithType(details.entity, entityType, translate);

        return (
            <Fragment>
                {createBoldElement(reviewer)}{' '}
                {translate('pages.job-architecture-v2.changelog.actions.assigned-reviewer')} {entityNode}
            </Fragment>
        );
    },
    comment_created: (details, entityType, translate) => {
        if (!isCommentCreatedDetail(details)) {
            return null;
        }
        const entityNode = formatEntityWithType(details.entity, entityType, translate);
        const actionKey = is.nullOrUndefined(details.comment.parentId)
            ? 'pages.job-architecture-v2.changelog.actions.comment-created'
            : 'pages.job-architecture-v2.changelog.actions.comment-replied';

        return (
            <Fragment>
                {translate(actionKey)} {entityNode}
            </Fragment>
        );
    },
    comment_resolved: (details, entityType, translate) => {
        if (!isCommentResolvedDetail(details)) {
            return null;
        }
        const entityNode = formatEntityWithType(details.entity, entityType, translate);

        return (
            <Fragment>
                {translate('pages.job-architecture-v2.changelog.actions.comment-resolved')} {entityNode}
            </Fragment>
        );
    },
    copied: (details, entityType, translate) => {
        if (!isCopiedDetail(details)) {
            return null;
        }
        const entityNode = formatEntityWithType(details.entity, entityType, translate);
        const fromNode = formatEntityWithType(details.from, 'cluster', translate);
        const toNode = formatEntityWithType(details.to, 'cluster', translate);

        return (
            <Fragment>
                {translate('pages.job-architecture-v2.changelog.actions.copied')} {entityNode}{' '}
                {translate('pages.job-architecture-v2.changelog.from')} {fromNode}{' '}
                {translate('pages.job-architecture-v2.changelog.to')} {toNode}
            </Fragment>
        );
    },
    created: (details, entityType, translate) => {
        if (!isCreateDetail(details)) {
            return null;
        }
        const entityNode = formatEntityWithType(details.entity, entityType, translate);
        if (!is.nullOrUndefined(details.parent)) {
            const parentNode = formatEntityWithType(details.parent, 'cluster', translate);

            return (
                <Fragment>
                    {translate('pages.job-architecture-v2.changelog.actions.created')} {entityNode}{' '}
                    {translate('pages.job-architecture-v2.changelog.in')} {parentNode}
                </Fragment>
            );
        }

        return (
            <Fragment>
                {translate('pages.job-architecture-v2.changelog.actions.created')} {entityNode}
            </Fragment>
        );
    },
    deleted: (details, entityType, translate) => {
        if (!isDeleteDetail(details)) {
            return null;
        }
        const entityNode = formatEntityWithType(details.entity, entityType, translate);
        if (!is.nullOrUndefined(details.parent)) {
            const parentNode = formatEntityWithType(details.parent, 'cluster', translate);

            return (
                <Fragment>
                    {translate('pages.job-architecture-v2.changelog.actions.deleted')} {entityNode}{' '}
                    {translate('pages.job-architecture-v2.changelog.from')} {parentNode}
                </Fragment>
            );
        }

        return (
            <Fragment>
                {translate('pages.job-architecture-v2.changelog.actions.deleted')} {entityNode}
            </Fragment>
        );
    },
    edited: (details, entityType, translate) => {
        if (!isEditDetail(details)) {
            return null;
        }
        const entityNode = formatEntityWithType(details.entity, entityType, translate);

        return (
            <Fragment>
                {translate('pages.job-architecture-v2.changelog.actions.edited')} {details.field}{' '}
                {translate('pages.job-architecture-v2.changelog.of')} {entityNode}{' '}
                {translate('pages.job-architecture-v2.changelog.from')} {details.oldValue}{' '}
                {translate('pages.job-architecture-v2.changelog.to')} {details.newValue}
            </Fragment>
        );
    },
    moved: (details, entityType, translate) => {
        if (!isMoveDetail(details)) {
            return null;
        }
        const entityNode = formatEntityWithType(details.entity, entityType, translate);
        const fromNode = formatEntityWithType(details.from, 'cluster', translate);
        const toNode = formatEntityWithType(details.to, 'cluster', translate);

        return (
            <Fragment>
                {translate('pages.job-architecture-v2.changelog.actions.moved')} {entityNode}{' '}
                {translate('pages.job-architecture-v2.changelog.from')} {fromNode}{' '}
                {translate('pages.job-architecture-v2.changelog.to')} {toNode}
            </Fragment>
        );
    },
    published: (details, entityType, translate) => {
        if (!isPublishDetail(details)) {
            return null;
        }
        const publisher = formatUserName(details.publisher, translate);
        const entityNode = formatEntityWithType(details.entity, entityType, translate);

        return (
            <Fragment>
                {createBoldElement(publisher)} {translate('pages.job-architecture-v2.changelog.actions.published')}{' '}
                {translate('pages.job-architecture-v2.changelog.version')} {details.version}{' '}
                {translate('pages.job-architecture-v2.changelog.of')} {entityNode}
            </Fragment>
        );
    },
    reviewed: (details, entityType, translate) => {
        if (!isReviewDetail(details)) {
            return null;
        }
        const reviewer = formatUserName(details.reviewer, translate);
        const entityNode = formatEntityWithType(details.entity, entityType, translate);

        return (
            <Fragment>
                {createBoldElement(reviewer)} {translate('pages.job-architecture-v2.changelog.actions.reviewed')}{' '}
                {translate('pages.job-architecture-v2.changelog.version')} {details.version}{' '}
                {translate('pages.job-architecture-v2.changelog.of')} {entityNode}
            </Fragment>
        );
    },
    generations_cancelled: (_details, _entityType, translate) =>
        translate('pages.job-architecture-v2.changelog.actions.generations-cancelled'),
    skill_bulk_removed: (details, _entityType, translate) => {
        if (!isSkillBulkRemovedDetail(details)) {
            return null;
        }

        return (
            <Fragment>
                {translate('pages.job-architecture-v2.changelog.actions.skill-bulk-removed')}{' '}
                {createBoldElement(details.bulkRemoval.skill.title)}
            </Fragment>
        );
    },
    skills_updated: (details, entityType, translate) => {
        if (!isSkillsUpdatedDetail(details)) {
            return null;
        }
        const entityNode = formatEntityWithType(details.entity, entityType, translate);

        return (
            <Fragment>
                {translate('pages.job-architecture-v2.changelog.actions.skills-updated')} {entityNode}
            </Fragment>
        );
    },
    task_created: (details, entityType, translate) => {
        if (!isTaskCreatedDetail(details)) {
            return null;
        }
        const entityNode = formatEntityWithType(details.entity, entityType, translate);

        return (
            <Fragment>
                {translate('pages.job-architecture-v2.changelog.actions.task-created')}{' '}
                {createBoldElement(details.task.title)} {translate('pages.job-architecture-v2.changelog.in')}{' '}
                {entityNode}
            </Fragment>
        );
    },
    task_deleted: (details, entityType, translate) => {
        if (!isTaskDeletedDetail(details)) {
            return null;
        }
        const entityNode = formatEntityWithType(details.entity, entityType, translate);

        return (
            <Fragment>
                {translate('pages.job-architecture-v2.changelog.actions.task-deleted')}{' '}
                {createBoldElement(details.task.title)} {translate('pages.job-architecture-v2.changelog.from')}{' '}
                {entityNode}
            </Fragment>
        );
    },
    task_edited: (details, entityType, translate) => {
        if (!isTaskEditedDetail(details)) {
            return null;
        }
        const entityNode = formatEntityWithType(details.entity, entityType, translate);

        return (
            <Fragment>
                {translate('pages.job-architecture-v2.changelog.actions.task-edited')}{' '}
                {createBoldElement(details.task.title)} {translate('pages.job-architecture-v2.changelog.of')}{' '}
                {entityNode}
            </Fragment>
        );
    },
    task_skills_updated: (details, entityType, translate) => {
        if (!isTaskSkillsUpdatedDetail(details)) {
            return null;
        }
        const entityNode = formatEntityWithType(details.entity, entityType, translate);

        return (
            <Fragment>
                {translate('pages.job-architecture-v2.changelog.actions.task-skills-updated')}{' '}
                {createBoldElement(details.task.title)} {translate('pages.job-architecture-v2.changelog.of')}{' '}
                {entityNode}
            </Fragment>
        );
    },
    tasks_reordered: (details, entityType, translate) => {
        if (!isTasksReorderedDetail(details)) {
            return null;
        }
        const entityNode = formatEntityWithType(details.entity, entityType, translate);

        return (
            <Fragment>
                {translate('pages.job-architecture-v2.changelog.actions.tasks-reordered')} {entityNode}
            </Fragment>
        );
    },
    unarchived: (details, entityType, translate) => {
        if (!isUnarchivedDetail(details)) {
            return null;
        }
        const entityNode = formatEntityWithType(details.entity, entityType, translate);

        return (
            <Fragment>
                {translate('pages.job-architecture-v2.changelog.actions.unarchived')} {entityNode}
            </Fragment>
        );
    },
    unassigned_reviewer: (details, entityType, translate) => {
        if (!isUnassignReviewerDetail(details)) {
            return null;
        }
        const reviewer = formatUserName(details.reviewer, translate);
        const entityNode = formatEntityWithType(details.entity, entityType, translate);

        return (
            <Fragment>
                {createBoldElement(reviewer)}{' '}
                {translate('pages.job-architecture-v2.changelog.actions.unassigned-reviewer')} {entityNode}
            </Fragment>
        );
    },
    version_created: (details, entityType, translate) => {
        if (!isVersionCreatedDetail(details)) {
            return null;
        }
        const entityNode = formatEntityWithType(details.entity, entityType, translate);

        return (
            <Fragment>
                {translate('pages.job-architecture-v2.changelog.actions.version-created')} {entityNode}
            </Fragment>
        );
    },
};

const ACTOR_PREFIXED_EVENT_TYPES: ReadonlySet<string> = new Set([
    'generations_cancelled',
    'archived',
    'comment_created',
    'comment_resolved',
    'created',
    'deleted',
    'edited',
    'moved',
    'copied',
    'unarchived',
    'task_created',
    'task_deleted',
    'task_edited',
    'task_skills_updated',
    'tasks_reordered',
]);

const generateRichTitle = (
    details: ChangeLogEventDetails,
    entityType: string,
    actor: { readonly firstName: string; readonly lastName: string } | null | undefined,
    translate: Translate,
): ReactNode => {
    const generator = isKnownChangeLogEventDetail(details) ? TITLE_GENERATORS[details.type] : undefined;

    if (is.function(generator)) {
        const titleNode = generator(details, entityType, translate);

        if (ACTOR_PREFIXED_EVENT_TYPES.has(details.type)) {
            const actorName = formatUserName(actor, translate);

            return (
                <Fragment>
                    {createBoldElement(actorName)} {titleNode}
                </Fragment>
            );
        }

        if (!is.nullOrUndefined(titleNode)) {
            return titleNode;
        }
    }

    const changedLabel = translate('pages.job-architecture-v2.changelog.actions.changed');

    if (is.nullOrUndefined(details.entity)) {
        return changedLabel;
    }

    return (
        <Fragment>
            {changedLabel} {formatEntityWithType(details.entity, entityType, translate)}
        </Fragment>
    );
};

const getSkillsUpdatedSubtitle = (
    details: ChangeLogEventSkillsUpdatedDetail | ChangeLogEventTaskSkillsUpdatedDetail,
    translate: Translate,
): ReactNode => {
    const groups: string[] = [];

    if (details.skillsAdded.length > 0) {
        const titles = details.skillsAdded.map((skill) => skill.title).join(', ');
        groups.push(`${translate('pages.job-architecture-v2.changelog.skills.added')}: ${titles}`);
    }
    if (details.skillsRemoved.length > 0) {
        const titles = details.skillsRemoved.map((skill) => skill.title).join(', ');
        groups.push(`${translate('pages.job-architecture-v2.changelog.skills.removed')}: ${titles}`);
    }
    if (details.skillsLevelChanged.length > 0) {
        const titles = details.skillsLevelChanged
            .map((skill) => `${skill.title} ${skill.oldLevel}→${skill.newLevel}`)
            .join(', ');
        groups.push(`${translate('pages.job-architecture-v2.changelog.skills.level-changed')}: ${titles}`);
    }

    return groups.join(' · ');
};

/**
 * The counts alone cannot say whether a scope was skipped or simply held nothing, so the receipt also
 * names the tenant-wide scopes the operator included. Events written before scopes were selectable
 * carry none, and then there is nothing to state.
 */
const getSkillBulkRemovedSubtitle = (
    details: ChangeLogEventSkillBulkRemovedDetail,
    translate: Translate,
): ReactNode => {
    const { counts, scopes } = details.bulkRemoval;
    const groups: string[] = [
        translate('pages.job-architecture-v2.skills.bulk-removal.receipt.counts', {
            employees: counts.employees,
            learningOpportunities: counts.learningOpportunities,
            levels: counts.levels,
            roles: counts.roles,
            tasks: counts.tasks,
        }),
    ];
    const includedScopes = [
        scopes?.learningOpportunities === true &&
            translate('pages.job-architecture-v2.skills.bulk-removal.receipt.scope-learning-opportunities'),
        scopes?.employees === true &&
            translate('pages.job-architecture-v2.skills.bulk-removal.receipt.scope-employees'),
        scopes?.tasks === true && translate('pages.job-architecture-v2.skills.bulk-removal.receipt.scope-tasks'),
    ].filter(is.string);

    if (is.nonEmptyArray(includedScopes)) {
        groups.push(
            translate('pages.job-architecture-v2.skills.bulk-removal.receipt.scopes-included', {
                scopes: includedScopes.join(', '),
            }),
        );
    }

    return groups.join(' · ');
};

const getEventSubtitle = (details: ChangeLogEventDetails, translate: Translate): ReactNode | undefined => {
    if (!isKnownChangeLogEventDetail(details)) {
        return undefined;
    }
    if (isSkillBulkRemovedDetail(details)) {
        return getSkillBulkRemovedSubtitle(details, translate);
    }
    if (isSkillsUpdatedDetail(details) || isTaskSkillsUpdatedDetail(details)) {
        return getSkillsUpdatedSubtitle(details, translate);
    }
    if (isTasksReorderedDetail(details)) {
        return details.tasks.map((task) => task.title).join(' · ');
    }
    if ('parent' in details && !is.nullOrUndefined(details.parent)) {
        return createBoldElement(details.parent.name);
    }
    if ('from' in details && 'to' in details) {
        return (
            <Fragment>
                {createBoldElement(details.from.name)} → {createBoldElement(details.to.name)}
            </Fragment>
        );
    }

    return undefined;
};

const groupEventsByDate = (events: readonly ChangeLogEvent[]): ReadonlyMap<string, readonly ChangeLogEvent[]> => {
    const grouped = new Map<string, ChangeLogEvent[]>();

    events.forEach((event) => {
        const date = formatDate(event.timestamp);
        if (!grouped.has(date)) {
            grouped.set(date, []);
        }
        grouped.get(date)?.push(event);
    });

    return grouped;
};

const ICON_MAP: Record<string, ReactElement> = {
    archived: <IconArchive />,
    assigned_reviewer: <IconPeople />,
    comment_created: <IconChatBubble7 />,
    comment_resolved: <IconChatBubble7 />,
    copied: <IconArrowsRepeat />,
    created: <IconCirclePlus />,
    deleted: <IconTrashCan />,
    edited: <IconPencil />,
    moved: <IconArrowsRepeat />,
    published: <IconSend />,
    reviewed: <IconCircleCheck />,
    skill_bulk_removed: <IconTrashCan />,
    skills_updated: <IconSkills />,
    task_created: <IconTasks />,
    task_deleted: <IconTasks />,
    task_edited: <IconTasks />,
    task_skills_updated: <IconTasks />,
    tasks_reordered: <IconTasks />,
    unarchived: <IconUnarchive />,
    unassigned_reviewer: <IconPeople />,
    version_created: <IconCirclePlus />,
};

const getIcon = (eventType: string): ReactElement => {
    return ICON_MAP[eventType] ?? <IconPencil />;
};

const formatDateLabel = (dateString: string, language: LanguageConfig, translate: Translate): string => {
    const date = parseISO(dateString);

    if (isToday(date)) {
        return translate('pages.job-architecture-v2.changelog.today');
    }
    if (isYesterday(date)) {
        return translate('pages.job-architecture-v2.changelog.yesterday');
    }

    return format(date, 'PPP', { locale: language.date.locale });
};

const getTimelineFooterItems = (
    event: ChangeLogEvent,
    language: LanguageConfig,
    translate: Translate,
): readonly TimelineFooterItem[] => {
    const footerItems: TimelineFooterItem[] = [];

    const actorName = formatUserName(event.actor, translate);
    if (is.nonEmptyString(actorName)) {
        footerItems.push({ content: actorName, key: 'person' });
    }

    const dateTime = new Date(event.timestamp);

    if (isToday(dateTime)) {
        const timeString = format(dateTime, 'p', { locale: language.date.locale });
        footerItems.push({ content: timeString, key: 'time' });
    } else {
        const dateTimeString = format(dateTime, 'PPp', { locale: language.date.locale });
        footerItems.push({ content: dateTimeString, key: 'time' });
    }

    return footerItems;
};

const transformEventToTimelineItem = (
    event: ChangeLogEvent,
    language: LanguageConfig,
    translate: Translate,
): TimelineItem => {
    return {
        id: event.eventId,
        entityId: event.itemId,
        entityType: event.entityType,
        eventDetails: event.details,
        footerItems: getTimelineFooterItems(event, language, translate),
        icon: getIcon(event.details.type),
        subtitle: getEventSubtitle(event.details, translate),
        title: generateRichTitle(event.details, event.entityType, event.actor, translate),
    };
};

const getTimelineGroups = (
    groupedData: ReadonlyMap<string, readonly ChangeLogEvent[]>,
    sortedDates: readonly string[],
    language: LanguageConfig,
    translate: Translate,
): readonly TimelineGroup[] => {
    return sortedDates.map((date, index) => {
        const events = groupedData.get(date) ?? [];

        return {
            date,
            isLastGroup: index === sortedDates.length - 1,
            items: events.map((event) => transformEventToTimelineItem(event, language, translate)),
            label: formatDateLabel(date, language, translate),
        };
    });
};

export const useChangelogData = (): UseChangelogDataReturn => {
    const historyQuery = useJobArchitectureHistory();

    const {
        applicationState: { language },
        translate,
    } = useContext(ApplicationContext);

    const events = useMemo(() => {
        if (is.nullOrUndefined(historyQuery.data?.pages)) {
            return [];
        }

        return historyQuery.data.pages.flatMap((page: PaginatedJobArchitectureHistory) => page.data);
    }, [historyQuery.data?.pages]);

    const groupedData = useMemo(() => groupEventsByDate(events), [events]);

    const sortedDates = useMemo(() => [...groupedData.keys()].sort((a, b) => b.localeCompare(a)), [groupedData]);

    const timelineGroups = useMemo(
        () => getTimelineGroups(groupedData, sortedDates, language, translate),
        [groupedData, sortedDates, language, translate],
    );

    return {
        hasNextPage: historyQuery.hasNextPage,
        isLoading: historyQuery.isLoading,
        LoadMoreCursor: historyQuery.LoadMoreCursor,
        timelineGroups,
    };
};
